<template>
  <ion-page>
    <WhiteHeader
      :back-button="true"
      route-back="/planning"
      title="Lavage Qualimat"
    />

    <ion-content>
      <ion-grid class="question">
        <h5>Type de lavage <span class="required">*</span></h5>
        <ReportSelect
          title="Type de lavage"
          v-model:value="kind"
          :options="PRODUCTS_LEVELS"
          :placeholders="['Choisissez un type de lavage']"
        />

        <template
          v-if="kind && products && products[kind] && products[kind].length"
        >
          <h5>Produit de lavage</h5>
          <ReportSelect
            title="Produit de lavage"
            v-model:value="product"
            :options="products[kind]"
            :placeholders="['Choisissez un produit de lavage']"
          />
        </template>

        <h5>Réalisé à <span class="required">*</span></h5>
        <Date v-model:date="date" />

        <h5>Commentaire</h5>
        <Textarea title="Commentaire" v-model:value="comment" />

        <h5>Pièces jointes</h5>
        <Attachments
          v-model:attachments="attachments"
          v-model:waiting-files="waitingFiles"
          v-model:attachments-to-delete="attachmentsToDelete"
          v-model:attachments-to-update="attachmentsToUpdate"
          type-vue="create"
          :files-too-big="false"
          title="Pièces jointes"
        />
      </ion-grid>
    </ion-content>

    <div class="bottom-buttons">
      <ion-button
        expand="block"
        class="next-step"
        :disabled="!kind || !date || sending"
        @click="save"
      >
        <ion-spinner v-if="sending" name="crescent" />

        <span v-if="sending">Sauvegarde en cours...</span>
        <span v-else>Sauvegarder</span>
      </ion-button>
    </div>
  </ion-page>
</template>

<script>
import { IonButton, IonContent, IonPage, IonSpinner } from '@ionic/vue';
import WhiteHeader from '@/components/driver/header/WhiteHeader';
import ReportSelect from '@/components/driver/rentalCoupon/report-components/ReportSelect.vue';
import Textarea from '@/components/driver/rentalCoupon/report-components/Textarea.vue';
import Attachments from '@/components/driver/rentalCoupon/components/Attachments.vue';
import { getParams } from '@/services/users';
import axios from 'axios';
import Date from '@/components/driver/rentalCoupon/report-components/Date.vue';
import dayjs from 'dayjs';
import { roundToFiveMinutes } from '@/services/utils/dateUtils';
import * as Sentry from '@sentry/vue';
import { sendErrorToast, sendSuccessToast } from '@/services/utils/toastUtils';
// import { getWaitingFilesFormData } from '@/services/utils/reportUtils';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const PRODUCTS_LEVELS = {
  A: 'A - Nettoyage à sec',
  B: "B - Nettoyage à l'eau",
  C: 'C - Nettoyage avec détergent',
  D: 'D - Désinfection (niveau maximal)',
};

export default {
  name: 'QualimatReportUpdate',

  components: {
    IonButton,
    IonContent,
    IonPage,
    IonSpinner,
    WhiteHeader,
    ReportSelect,
    Textarea,
    Date,
    Attachments,
  },

  setup() {
    return {
      PRODUCTS_LEVELS,
    };
  },

  watch: {
    kind() {
      this.product = '';
    },
  },

  data() {
    return {
      kind: '',
      product: '',
      comment: '',
      date: null,
      attachments: [],

      // fetched
      products: [],
      sending: false,

      // files
      waitingFiles: [],
      attachmentsToDelete: [],
      attachmentsToUpdate: {},
    };
  },

  ionViewWillEnter() {
    this.resetForm();

    const now = dayjs();
    const roundedMinutes = roundToFiveMinutes(now.minute());
    this.date = now
      .minute(roundedMinutes)
      .second(0)
      .millisecond(0)
      .toISOString();

    this.getProducts();

    if (this.$route.params.id) {
      this.getReport();
    }
  },

  methods: {
    resetForm() {
      this.kind = '';
      this.product = '';
      this.comment = '';
      this.date = null;
      this.attachments = [];
      this.products = [];
      this.waitingFiles = [];
      this.attachmentsToDelete = [];
      this.attachmentsToUpdate = {};
      this.sending = false;
    },

    async getReport() {
      try {
        const response = await axios.get(
          `/driver/qualimat-report/${this.$route.params.id}`,
          await getParams(),
        );

        const report = response.data;
        this.kind = report.kind;
        this.product = report.product;
        this.comment = report.comment;
        this.attachments = report.attachments || [];

        // ensure date is converted to local timezone
        this.date = dayjs.utc(report.achieved_at).local().toISOString();
      } catch (error) {
        if (process.env.VUE_APP_SENTRY_DSN) {
          Sentry.captureException(error);
        } else {
          console.error(error);
        }

        sendErrorToast('Erreur lors de la récupération du rapport');
      }
    },

    async getProducts() {
      try {
        const response = await axios.get(
          '/driver/qualimat-products',
          await getParams(),
        );

        this.products = response.data;
      } catch (error) {
        if (process.env.VUE_APP_SENTRY_DSN) {
          Sentry.captureException(error);
        } else {
          console.error(error);
        }

        sendErrorToast('Erreur lors de la récupération des produits');
      }
    },

    async save() {
      if (this.sending) {
        return;
      }

      if (!this.kind) {
        sendErrorToast('Veuillez sélectionner un type de lavage');
        return;
      }

      this.sending = true;

      try {
        const url = this.$route.params.id
          ? `/driver/qualimat-report/${this.$route.params.id}`
          : '/driver/qualimat-report';

        console.log('Saving report with data:', {
          achieved_at: this.date,
        });

        const formData = this.getWaitingFilesFormData(this.waitingFiles);
        formData.append(
          'report_data',
          JSON.stringify({
            kind: this.kind,
            product: this.product,
            comment: this.comment,
            dump_body_id: this.$route.params.dumpBodyId,
            achieved_at: this.date,
            attachmentsToDelete: this.attachmentsToDelete,
            attachmentsToUpdate: this.attachmentsToUpdate,
          }),
        );

        await axios.post(url, formData, await getParams());

        sendSuccessToast('Rapport Qualimat sauvegardé avec succès');

        // if we can go back, do it. Otherwise, go to planning page
        if (this.$router.options.history.state.back) {
          this.$router.back();
        } else {
          this.$router.push('/planning');
        }
      } catch (error) {
        if (process.env.VUE_APP_SENTRY_DSN) {
          Sentry.captureException(error);
        } else {
          console.error(error);
        }

        sendErrorToast('Erreur lors de la sauvegarde du rapport Qualimat');
      } finally {
        this.sending = false;
      }
    },

    getWaitingFilesFormData(waitingFiles) {
      const formData = new FormData();

      // add all waiting files to our formData
      Object.values(waitingFiles).forEach(waitingFile => {
        formData.append(
          'attachments[]',
          waitingFile,
          waitingFile.newName || waitingFile.name,
        );
      });

      formData.append('keys[]', 'attachments');

      return formData;
    },
  },
};
</script>

<style scoped lang="scss">
ion-content {
  --padding-start: 0;
  --padding-end: 0;
  --padding-bottom: 0;
  --padding-top: 0;
  --background: #fffcfa;
}

.mission-report-container {
  padding: 0 16px;
  padding-bottom: 100px; /* Add padding to prevent content from being hidden behind bottom bar */
}

h3 {
  margin-top: 32px;
  font-size: 16px;
  font-weight: bold;
}

.question {
  padding: 0 8px;
}

.next-step {
  --background: #fcd34d !important;
}

.required {
  color: red;
}
</style>
